import { ConversationMediaItem } from "./media";

export type ConversationChannel = "phone" | "web" | "link" | "app" | "email" | "meeting" | "slack" | "teams";

export type ConversationType = "regular" | "setup" | "mailbox";

/** conversations.attention: how much a listened call or forwarded email needs someone. */
export type ConversationAttention = "urgent" | "high" | "normal";

/** The central inbox's queues (api conversations/list `queue`); "all" sends none. */
export type InboxQueue = "all" | "attention" | "mine" | "handled";

/** A user's remembered Conversations inbox filters (profiles.inbox_filters); null means "all". */
export interface InboxFilters {
  agentId: string | null;
  channel: ConversationChannel | null;
  viewId: string | null;
  timeRange: string | null;
  queue: Exclude<InboxQueue, "all"> | null;
}

export interface Conversation {
  id: string;
  agent_id: string;
  elevenlabs_id: string;
  el_agent_id: string;
  type?: ConversationType | string | null;
  viewed?: boolean;
  viewed_at?: string | null;
  /** Listener genies: someone answered this call or forwarded email, or marked it handled. */
  handled_at?: string | null;
  /** Listener genies: needs someone, and how much; null when nothing needs doing. */
  attention?: ConversationAttention | null;
  /** Teammate taking this conversation; their reminders go to them only. */
  assigned_to?: string | null;
  assigned_at?: string | null;
  metadata?: any;
  isSynced: boolean;
  details: ConversationMeta;
  agent: any;
  medias?: ConversationMediaItem[];
}

export type ConversationDetailsProps = {
  isOpen: boolean;
  onClose: () => void;
} & (
  | { conversation: Conversation; conversationId?: never }
  | { conversation?: never; conversationId: string }
);

export interface ConversationListCache {
  pages: Array<{ data: Conversation[] }>;
}

export interface ConversationMeta {
  id: string;
  conv_id: number;
  agent_id: string;
  status?: string | null;
  duration?: number | null;
  summary?: string | null;
  transcript?: any | null;
  goals?: string[] | null;
  topics?: string[] | null;
  lead_info?: any | null;
  analysis_result?: any | null;
  conversation_date?: string | null;
  last_synced_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  escalation?: {
    reason?: string | null;
    severity?: "low" | "medium" | "high" | null;
    [key: string]: unknown;
  } | null;
}

export interface ConversationDetails {
  id?: string;
  elevenlabs_conversation_id?: string;
  agent_id?: string | null;
  genie_name?: string | null;
  genie_branding?: any | null;
  conversation_date?: string | null;
  duration_seconds?: number | null;
  summary?: string | null;
  goals?: string[] | null;
  topics?: string[] | null;
  lead_info?: any | null;
  analysis_result?: any | null;
  status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  conversation_id?: number | null;
  last_synced_at?: string | null;
  transcript?: any | null;
  escalation?: any | null;
  has_analysis?: boolean;
}

export interface ConversationAnalysisOptions {
  page?: number;
  pageSize?: number;
  agentId?: string;
  analysisFilter?: "all" | "analyzed" | "unanalyzed";
  dateRange?: string;
  agents?: Array<{ id: string; name: string; elevenlabs_id?: string; branding?: any }>;
}

export type ConversationViewMode = "cards" | "rows" | "table";

export interface ConversationAnalysisResult {
  talk_metrics?: {
    agent_talk_time: number;
    customer_talk_time: number;
    silence_time: number;
    agent_talk_percentage: number;
    customer_talk_percentage: number;
    silence_percentage: number;
  };
  sentiment_analysis?: {
    overall_sentiment_type: "positive" | "neutral" | "negative";
    overall_sentiment_score: number;
    customer_satisfaction_score: number;
  };
  main_topics?: string[];
  key_insights?: string[];
  brief_summary?: string;
  lead_info?: Record<string, string>;
  recommendations?: string[];
  custom_analysis?: string;
  issue_category?: IssueCategory;
  resolution_status?: ResolutionStatus;
  error?: string;
}

export const ISSUE_CATEGORIES = [
  "billing",
  "technical_support",
  "scheduling",
  "product_info",
  "complaint",
  "sales_inquiry",
  "account_management",
  "other",
] as const;

export type IssueCategory = (typeof ISSUE_CATEGORIES)[number];

export const ISSUE_CATEGORY_LABELS: Record<IssueCategory, string> = {
  billing: "Billing",
  technical_support: "Technical Support",
  scheduling: "Scheduling",
  product_info: "Product Info",
  complaint: "Complaint",
  sales_inquiry: "Sales Inquiry",
  account_management: "Account Management",
  other: "Other",
};

export type ResolutionStatus = "resolved" | "partial" | "unresolved";

export interface ConversationsPageProps {
  embedded?: boolean;
  /** Receives the conversations on screen, so the Inbox can hand them to "Ask about your inbox". */
  onConversationsChange?: (conversations: Conversation[]) => void;
}

export interface InboxTabContentProps {
  tabId: string;
  onConversationsChange?: (conversations: Conversation[]) => void;
}
